import { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../services/api/axiosConfig';
import { ToolCardJob } from '../types/chat';
import { isJobNotFound, MAX_POLL_FAILURES, VIDEO_JOB_POLL_MS } from './useVideoJobCard';

export interface CarouselSlideState {
  job_id: string;
  slide_index: number;
  role: string | null;
  status: string;
  progress: number;
  image: { id?: number; url: string } | null;
  error_message: string | null;
}

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);
const terminalCache = new Map<string, CarouselSlideState>();

/** 状態取得を諦めたスライドに出す文言。 */
const FETCH_FAILED_MESSAGE = '状態を取得できませんでした';

interface ImageJobResponse {
  job?: { status?: string; progress?: number; error_message?: string | null };
  images?: { id?: number; url?: string }[];
}

const initial = (job: ToolCardJob): CarouselSlideState =>
  terminalCache.get(job.job_id) ?? {
    job_id: job.job_id, slide_index: job.slide_index, role: job.role ?? null,
    status: job.status ?? 'pending', progress: 0, image: null, error_message: null,
  };

/**
 * カルーセルカード用: 各スライドの GET /images/jobs/{id} を進行中の間 5 秒ごとに読む。
 * 終端スライドはキャッシュして再取得しない。
 * 取得失敗は MAX_POLL_FAILURES 回まで次のポーリングでリトライし、超えたら failed として打ち切る
 * （404 は即打ち切り）。これにより恒久失敗でもリクエストが無限に続かない。
 */
export function useCarouselJobCards(jobs: ToolCardJob[] | undefined, enabled: boolean): { slides: CarouselSlideState[]; allDone: boolean } {
  const key = useMemo(() => (jobs ?? []).map((j) => j.job_id).join(','), [jobs]);
  const [slides, setSlides] = useState<CarouselSlideState[]>(() => (jobs ?? []).map(initial));

  useEffect(() => {
    setSlides((jobs ?? []).map(initial));
    if (!enabled || !jobs || jobs.length === 0) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // スライドごとの連続失敗回数。成功したら 0 に戻す。
    const failures = new Map<string, number>();

    const applySlide = (jobId: string, next: CarouselSlideState) => {
      if (TERMINAL.has(next.status)) terminalCache.set(jobId, next);
      setSlides((prev) => prev.map((s) => (s.job_id === jobId ? next : s)));
    };

    const tick = async () => {
      const pending = jobs.filter((j) => !terminalCache.has(j.job_id));
      await Promise.all(pending.map(async (job) => {
        try {
          const response = await axiosInstance.get(`/images/jobs/${job.job_id}`);
          if (cancelled) return;
          failures.set(job.job_id, 0);
          const data = response.data as ImageJobResponse;
          const first = (data.images ?? []).find((img) => Boolean(img?.url));
          const next: CarouselSlideState = {
            job_id: job.job_id, slide_index: job.slide_index, role: job.role ?? null,
            status: data.job?.status ?? 'processing',
            progress: typeof data.job?.progress === 'number' ? data.job.progress : 0,
            image: first?.url ? { id: first.id, url: first.url } : null,
            error_message: data.job?.error_message ?? null,
          };
          applySlide(job.job_id, next);
        } catch (err) {
          if (cancelled) return;
          const count = (failures.get(job.job_id) ?? 0) + 1;
          failures.set(job.job_id, count);
          // 上限まではそのまま次のポーリングに任せる（404 は回復しないので即打ち切り）。
          if (!isJobNotFound(err) && count < MAX_POLL_FAILURES) return;
          applySlide(job.job_id, {
            job_id: job.job_id, slide_index: job.slide_index, role: job.role ?? null,
            status: 'failed', progress: 0, image: null, error_message: FETCH_FAILED_MESSAGE,
          });
        }
      }));
      if (cancelled) return;
      if (jobs.some((j) => !terminalCache.has(j.job_id))) {
        timer = setTimeout(tick, VIDEO_JOB_POLL_MS);
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  const allDone = slides.length > 0 && slides.every((s) => TERMINAL.has(s.status));

  return { slides: [...slides].sort((a, b) => a.slide_index - b.slide_index), allDone };
}

export function __clearCarouselJobCache(): void {
  terminalCache.clear();
}
